import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, InputNumber, Checkbox, Row, Col, Modal, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import useLocationOptions from "../../../hooks/useLocationOptions";

const { Option } = Select;
const { TextArea } = Input;

const BasicDetails = ({ initialData, onNext, onBack }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [engagements, setEngagements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedEngagementId, setSelectedEngagementId] = useState(initialData?.engagementId || null);

  // Fetch customer's engagements on mount
  useEffect(() => {
    const fetchEngagements = async () => {
      setLoadingEngagements(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          API_CONST.GET_ENGAGEMENTS,
          { status: 'active' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEngagements(response.data.engagements || []);
      } catch (error) {
        console.error("Error fetching engagements:", error);
        setEngagements([]);
      } finally {
        setLoadingEngagements(false);
      }
    };
    fetchEngagements();
  }, []);

  // Set form value when engagements load and engagementId is from URL
  useEffect(() => {
    if (initialData?.engagementId && engagements.length > 0) {
      const engagementExists = engagements.find(e => e.id === initialData.engagementId);
      if (engagementExists) {
        form.setFieldsValue({ engagementId: initialData.engagementId });
        setSelectedEngagementId(initialData.engagementId);
      }
    }
  }, [engagements, initialData?.engagementId, form]);

  // Fetch projects when engagement changes
  useEffect(() => {
    const fetchProjects = async () => {
      if (!selectedEngagementId) {
        setProjects([]);
        return;
      }
      setLoadingProjects(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.post(
          API_CONST.GET_PROJECTS,
          { engagementId: selectedEngagementId, status: 'active' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [selectedEngagementId]);

  const handleEngagementChange = (engagementId) => {
    setSelectedEngagementId(engagementId);
    form.setFieldsValue({ projectId: null });
    
    const selectedEngagement = engagements.find(e => e.id === engagementId);
    if (selectedEngagement) {
      // Pre-fill duration from engagement
      if (selectedEngagement.expectedDuration && selectedEngagement.durationUnit) {
        const durationText = `${selectedEngagement.expectedDuration} ${selectedEngagement.durationUnit === 'months' ? (selectedEngagement.expectedDuration === 1 ? 'Month' : 'Months') : selectedEngagement.durationUnit}`;
        form.setFieldsValue({ monthsOfEngagement: durationText });
      }
      // Pre-fill engagement type
      if (selectedEngagement.engagementType) {
        form.setFieldsValue({ engagementType: selectedEngagement.engagementType });
      }
    }
  };

  const handleSubmit = (values) => {
    onNext(values);
  };

  const handleCancel = () => {
    Modal.confirm({
      title: "Cancel Job Posting",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to cancel?</p>
          <p style={{ color: "#666", fontSize: 13 }}>
            All unsaved changes will be lost.
          </p>
        </div>
      ),
      okText: "Yes, Cancel",
      okButtonProps: { danger: true },
      cancelText: "Continue Editing",
      onOk: () => {
        navigate("/customer/my-jobs");
      },
    });
  };

  const { countries, cities, fetchCities } = useLocationOptions(initialData?.reportingCountry || "India");

  const handleReportingCountryChange = (country) => {
    fetchCities(country);
    if (country === "Remote") {
      form.setFieldsValue({ reportingLocation: "Remote" });
    } else {
      form.setFieldsValue({ reportingLocation: undefined });
    }
  };

  const engagementMonths = [
    "1 Month",
    "2 Months",
    "3 Months",
    "6 Months",
    "9 Months",
    "12 Months",
    "18 Months",
    "24 Months",
  ];

  const engagementTypes = [
    "Fixed Price Project",
    "Time & Material",
    "Dedicated Team",
    "Staff Augmentation",
    "Managed Services",
    "Consulting",
  ];

  const communicationLevels = [
    "Excellent",
    "Good",
    "Average",
    "Basic",
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{ ...initialData, budgetPeriod: "Per/month", numberOfRequirementsPeriod: "Per/month" }}
    >
      <div className="two-column-grid" style={{ marginBottom: 16 }}>
        <Form.Item
          name="engagementId"
          label="Engagement"
          rules={[{ required: true, message: "Please select an engagement" }]}
        >
          <Select
            placeholder="Select engagement"
            onChange={handleEngagementChange}
            loading={loadingEngagements}
            notFoundContent={loadingEngagements ? <Spin size="small" /> : "No active engagements"}
            showSearch
            optionFilterProp="label"
            options={engagements.map((eng) => ({ label: `${eng.id} - ${eng.title}`, value: eng.id }))}
          />
        </Form.Item>

        <Form.Item
          name="projectId"
          label="Project (Optional)"
        >
          <Select
            placeholder="Select project"
            allowClear
            disabled={!selectedEngagementId}
            loading={loadingProjects}
            notFoundContent={loadingProjects ? <Spin size="small" /> : "No active projects"}
            showSearch
            optionFilterProp="label"
            options={projects.map((proj) => ({ label: `${proj.id} - ${proj.title}`, value: proj.id }))}
          />
        </Form.Item>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Form.Item
            name="reportingCountry"
            label="Country"
            rules={[{ required: true, message: "Please select country" }]}
            initialValue="India"
          >
            <Select onChange={handleReportingCountryChange} showSearch optionFilterProp="children">
              {countries.map((c) => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="reportingLocation"
            label="Reporting Location"
            rules={[{ required: true, message: "Please select location" }]}
          >
            <Select placeholder="Select City" showSearch optionFilterProp="children">
              {cities.map((c) => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <div className="four-column-grid">
        <Form.Item
          name="budget"
          label="Budget"
          rules={[{ required: true, message: "Please enter budget" }]}
          extra="₹ (this Price will be shown to Developers)"
        >
          <InputNumber
            placeholder="Enter your price"
            style={{ width: "100%" }}
            min={0}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => (value ? value.replace(/,/g, "") : "")}
          />
        </Form.Item>

        <Form.Item name="budgetPeriod" label=" " initialValue="Per/month">
          <Select>
            <Option value="Per/month">Per/month</Option>
            <Option value="Per/hour">Per/hour</Option>
            <Option value="Fixed Price">Fixed Price</Option>
          </Select>
        </Form.Item>

        <Form.Item name="worldwide" valuePropName="checked" label=" ">
          <Checkbox style={{ marginTop: '8px' }}>Worldwide</Checkbox>
        </Form.Item>
      </div>

      <div className="four-column-grid">
        <Form.Item
          name="monthsOfEngagement"
          label="Month of Engagement"
          rules={[
            { required: true, message: "Please select engagement period" },
          ]}
        >
          <Select placeholder="3 Months">
            {engagementMonths.map((month) => (
              <Option key={month} value={month}>
                {month}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="engagementType"
          label="Engagement Type"
          rules={[{ required: true, message: "Please select type" }]}
        >
          <Select placeholder="Full-Time Contract">
            {engagementTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="numberOfRequirements"
          label="No of Requirements"
          rules={[{ required: true, message: "Please enter number" }]}
        >
          <InputNumber
            placeholder="Enter your price"
            style={{ width: "100%" }}
            min={1}
          />
        </Form.Item>

        <Form.Item name="numberOfRequirementsPeriod" label=" " initialValue="Per/month">
          <Select>
            <Option value="Per/month">Per/month</Option>
            <Option value="Total">Total</Option>
          </Select>
        </Form.Item>
      </div>

      <div className="two-column-grid">
        <Form.Item
          name="tentativeStartDate"
          label="Tentative Start Date"
          rules={[{ required: true, message: "Please enter start date" }]}
        >
          <Input
            type="date"
            placeholder="e.g. Chennai, Tamilnadu, Kolkata"
          />
        </Form.Item>
        <Form.Item
          name="communication"
          label="Communication (EN)"
          rules={[
            { required: true, message: "Please select communication level" },
          ]}
        >
          <Select placeholder="Excellent">
            {communicationLevels.map((level) => (
              <Option key={level} value={level}>
                {level}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className="form-actions" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <Button onClick={onBack}>
            Back
          </Button>
          <Button type="primary" htmlType="submit">
            Save & Next
          </Button>
        </div>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default BasicDetails;
